import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { DepDateContext } from "../../contexts/dep-date-context";
import OrderSummaryContext from "../../contexts/order-summary-context";
import { useCart } from "../../hooks/useCart";
import { getLanguageCode } from "../../utils/getURLParams";
import StandardFieldset from "../UI/StandardFieldset";
import StandardFormItem from "../UI/StandardFormItem";
import StandardH2 from "../UI/StandardH2";
import StandardInfo from "../UI/StandardInfo";
import StandardInputWrapper from "../UI/StandardInputWrapper";
import StandardLabel from "../UI/StandardLabel";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { ABContext } from "../../contexts/ab-context";
import ReactModal from 'react-modal';
import { formatCurrency, getGeo } from "../../utils/formatCurrency";
import DepositsCopy from "./DepositsCopy";
import PaymentOption from "./PaymentOption";

const PaymentOptions = (props) => {

  const config = getDomainConfig();
  const { t } = useTranslation('common');
  const geo = getGeo();

/* LOADING THE BRANDED SCSS */
const [styles, setStyles] = useState(false);

useEffect(() => {

const getStyles = async (theFileName, theFileEnd) => {
  if (config.brand) {
    let moduleStyles = false;
    try {
      moduleStyles = await import(
        "./" + theFileName + config.brand + theFileEnd
      );
      setStyles(moduleStyles);
    } catch (e) {
      console.error(
        `Styles for ${theFileName} could not be loaded, ${e.message}`
      );
    }
  }
};

  // Specify file name and end of file name here.
  getStyles("PaymentOptions", ".module.scss");
}, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const abContext = useContext(ABContext);

  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const { orderSummary, setOrderSummary } = useContext(OrderSummaryContext);
  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();
  const depDateContext = useContext(DepDateContext);
  const [depAvailable, setDepAvailable] = useState(true);
  const [paymentOptionSelected, setPaymentOptionSelected] = useState({
    full: true,
    deposit: false,
    non_refundable_deposit: false
  });
  const [depositAmount, setDepositAmount] = useState(0);
  let totalPrice = 0;
  let nonRefundableDepositAmount = 0;
  let hotelResortId = 0;

  let cartMeta = [];
  if (cartIsSuccess) {
    cartMeta = cartContents.meta;
    totalPrice = cartMeta.total_price;

    if (cartMeta.is_hotel_cart) {
      hotelResortId = cartMeta.payment_options.non_refundable_deposit?.hotel_resort_id;
      nonRefundableDepositAmount = cartMeta.payment_options.non_refundable_deposit?.amount || 0;
    }

    if (cartMeta.total_discount) {
      totalPrice = totalPrice - parseFloat(cartMeta.total_discount).toFixed(2);
    }

    if (+cartMeta?.total_hotel_tax > 0) {
      totalPrice = +totalPrice + +cartMeta.total_hotel_tax;
    }

    // Add delivery charges if there are any.
    if (orderSummary.deliveryCharges) {
      totalPrice = parseFloat(Number(totalPrice) + Number(orderSummary.deliveryCharges.price)).toFixed(2);
    }
    if (cartMeta.total_gift_card_balance > 0){
      totalPrice =  parseFloat(Number(totalPrice) - Number(cartMeta.total_gift_card_balance)).toFixed(2);
    }
  }

  const handlePaymentChange = (event) => {
    // Adding another layer of protection incase someone tries to remove disabled input parameter
    if ((event.target.value === "deposit") && (!depAvailable && isCro1381)) {
      setPaymentOptionSelected({
        full: true,
        deposit: false,
        non_refundable_deposit: false
      });
      setOrderSummary({ ...orderSummary, paymentOption: "full" });
      return;
    }

    if (event.target.value === "non-refundable-deposit") {
      setPaymentOptionSelected({
        full: false,
        deposit: false,
        non_refundable_deposit: true
      });
      googleDataLayer.createPaymentOptionChoiceEvent(event.target.value, depositAmount.toFixed(2));
    }

    if (event.target.value === "full") {
      setPaymentOptionSelected({
        full: true,
        deposit: false,
        non_refundable_deposit: false
      });
      googleDataLayer.createPaymentOptionChoiceEvent(event.target.value, (+totalPrice < 0 ? 0 : parseFloat(Number(totalPrice)).toFixed(2)))
    }
    if (event.target.value === "deposit") {
      setPaymentOptionSelected({
        full: false,
        deposit: true,
        non_refundable_deposit: false
      });
      googleDataLayer.createPaymentOptionChoiceEvent(event.target.value, depositAmount.toFixed(2))
    }
    setOrderSummary({ ...orderSummary, paymentOption: event.target.value });
  };

  useEffect(() => {
    if (cartIsSuccess) {
      const depositDays = cartMeta.payment_options.days;
      const cutoffDate = new Date(Date.parse(depDateContext.depDate));
      cutoffDate.setDate(cutoffDate.getDate() - parseInt(depositDays));

      if ((+cartMeta.payment_options.deposit_enabled + +cartMeta.payment_options.qualify) < 2) {
        setDepAvailable(false);
        // Make sure Full Payment is selected.
        document.getElementById("payment-option-full").click();
      } else if (cutoffDate < new Date()) {
        setDepAvailable(false);
        // Make sure Full Payment is selected.
        document.getElementById("payment-option-full").click();
      } else {
        setDepAvailable(true);
      }
    }
  }, [depDateContext, cartContents, cartIsSuccess]);

  useEffect(() => {
    if (totalPrice < 0) setDepAvailable(false);
  },[cartIsSuccess]);

  useEffect(()=>{
    if (cartIsSuccess && cartMeta){
      let deposit = +cartMeta.payment_options.deposittotal - +cartMeta.total_gift_card_balance;
      if (+cartMeta.total_gift_card_balance >= +cartMeta.payment_options.deposittotal) {
        setDepositAmount(0);
      }
      else {
        setDepositAmount(deposit);
      }
    }
  },[cartContents])

  const [isModalOpen, setIsModalOpen] = useState(false); // Modal

  let defaultClass = styles.radioInput; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show

  useEffect(() => {
    if (isCro1381 && depAvailable) {
      // Programmatically click the deposit radio button
      let depositRadioButton = document.getElementById('payment-option-deposit');

      if(document.getElementById('payment-option-non-refundable-deposit')){
        depositRadioButton = document.getElementById('payment-option-non-refundable-deposit');
      }
      setTimeout(() => {
        if (depositRadioButton) {
          depositRadioButton.click();
        }
      }, -1);
    }
  }, [isCro1381, depAvailable]);

  // Get the final deposit date
  const domainLang = getLanguageCode(config.brand);
  const calculateDate = () => {
    const depDate = new Date(depDateContext.depDate);
    const daysToSubtract = cartMeta.payment_options.days;

    depDate.setDate(depDate.getDate() - daysToSubtract);

    // Adding this for Uo (and SW eventually) so we can set a locale for the deposit days calculations
    // if (config.brand === 'Uo') {
    //   return depDate.toLocaleDateString('en-GB', {
    //     day: 'numeric',
    //     month: 'long',
    //     year: 'numeric'
    //   });
    // }

    return depDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const depositTabCopy = (hotelResortId) => {
    switch (hotelResortId) {
      case 481:
        return t("refundable-wdw-tab-message");
      default:
        return t("deposits-message-1", { date: calculateDate() });
    }
  };

  return (
    <StandardFieldset id="payment-options-pane">
      {isCro1381 ? (
        <StandardH2 className="cro1381H2">
          {domainLang === "de" ? t("payment-options") : t("how-would-you-like")}
        </StandardH2>
      ) : (
        <StandardH2>{t("payment-options")}</StandardH2>
      )}

      {cartIsSuccess && (
        <React.Fragment>
          <div className={styles.radioHolder} id="payment-options-wrapper">
            <StandardFormItem
              className="radioItem"
              id="payment-option-full-container"
            >
              <StandardLabel
                selected={paymentOptionSelected.full}
                htmlFor="payment-option-full"
                className="radioLabel radioLabelFullpay"
              >
                {isCro1381 ? (
                  <div className={styles.radioButtonContainer}>
                    <div className={styles.radioBtn}>
                      {paymentOptionSelected.full && (
                        <div className={styles.radioBtnSelected}></div>
                      )}
                    </div>
                    <span className={styles.label}>{t("full-payment")}</span>
                  </div>
                ) : (
                  <span className={styles.label}>{t("full-payment")}</span>
                )}
                <span id="full-payment-amount" className={styles.amount}>
                  {" "}
                  {+totalPrice < 0
                    ? 0
                    : formatCurrency(
                        parseFloat(Number(totalPrice)).toFixed(2),
                        geo
                      )}{" "}
                </span>
              </StandardLabel>
              <StandardInputWrapper className="radioWrapper">
                <input
                  type="radio"
                  id="payment-option-full"
                  name="payment-option"
                  value="full"
                  className={classes}
                  defaultChecked={true}
                  onChange={handlePaymentChange}
                />
                <span></span>
              </StandardInputWrapper>
            </StandardFormItem>
            {((isCro1381 &&
              (domainLang === "en" ||
                domainLang === "en-ie" ||
                domainLang === "au")) ||
              depAvailable) && (
              <>
                <PaymentOption
                  name={"deposit"}
                  styles={styles}
                  classes={classes}
                  isDepositAvailable={depAvailable}
                  paymentOptionSelected={paymentOptionSelected.deposit}
                  formatCurrency={formatCurrency}
                  depositAmount={+depositAmount}
                  geo={geo}
                  depDateContext={depDateContext}
                  cartMeta={cartMeta}
                  handlePaymentChange={handlePaymentChange}
                  calculateDate={calculateDate}
                  currency={props.currency}
                  hotelResortId={hotelResortId}
                  paymentOptionTitle={((+nonRefundableDepositAmount > 0) && depAvailable) ? t("RefundableDeposit") : t("Deposit")}
                  paymentOptionSubtext={depositTabCopy(hotelResortId)}
                  shouldDisplay={true}
                  brand={config.brand}
                />
                <PaymentOption
                  name={"non-refundable-deposit"}
                  styles={styles}
                  classes={classes}
                  isDepositAvailable={depAvailable}
                  paymentOptionSelected={paymentOptionSelected.non_refundable_deposit}
                  formatCurrency={formatCurrency}
                  depositAmount={+nonRefundableDepositAmount}
                  geo={geo}
                  depDateContext={depDateContext}
                  cartMeta={cartMeta}
                  handlePaymentChange={handlePaymentChange}
                  calculateDate={calculateDate}
                  currency={props.currency}
                  hotelResortId={hotelResortId}
                  paymentOptionTitle={t("non-refundable-deposit")}
                  paymentOptionSubtext={t("non-refundable-deposits-tab-message", {amount: formatCurrency(+nonRefundableDepositAmount.toFixed(2), geo)})}
                  shouldDisplay={((+nonRefundableDepositAmount > 0) && depAvailable)}
                  brand={config.brand}
                />
              </>
            )}
          </div>
          <div id="deposit-information">
            {depAvailable && !isCro1381 && (
              <>
                <StandardInfo>
                  {cartMeta.payment_options?.message_notavailable}{" "}
                  {isCro1381 && (
                    <p
                      className={styles.depositsLearnMore}
                      onClick={() => setIsModalOpen(!isModalOpen)}
                    >
                      Learn more
                    </p>
                  )}
                </StandardInfo>
                <ReactModal
                  isOpen={isModalOpen}
                  onRequestClose={() => setIsModalOpen(false)}
                  className={styles.depositsModal}
                  overlayClassName={styles.depositsModalOverlay}
                  shouldCloseOnOverlayClick={true}
                  shouldCloseOnEsc={true}
                  htmlOpenClassName={styles.tester}
                  onAfterOpen={() => {
                    document.body.style.overflow = "hidden";
                  }}
                  onAfterClose={() => {
                    document.body.style.overflow = "unset";
                  }}
                >
                  <div className={styles.depositsModalHeader}>
                    <StandardH2>{t("deposits-title")}</StandardH2>
                    <div
                      className={styles.depositsCloseButton}
                      onClick={() => setIsModalOpen(false)}
                    ></div>
                  </div>
                  <DepositsCopy
                    styles={styles}
                    currency={props.currency}
                    amount={depositAmount}
                    formatCurrency={formatCurrency}
                    geo={geo}
                    cutOffDate={calculateDate}
                    standardCopy={true}
                  />
                </ReactModal>
              </>
            )}
          </div>
        </React.Fragment>
      )}
    </StandardFieldset>
  );
};

export default PaymentOptions;
